import type { CaseCopy } from "./types";

export const sepsis: CaseCopy = {
  slug: "sepsis",
  displayName: "Sepsis Cases — Dutch Hospital ER Triage",
  issueNumberRoman: "III",
  issueNumberArabic: "03",
  mastheadIssue: "Issue III · Apr 2026",

  noteTagline: <>What follows is one such reconstruction, applied to a hospital emergency room.</>,

  sourceCard: (
    <>
      The data comes from the <b>Sepsis Cases</b> log — <b>1,050 patient trajectories</b> through
      the emergency department of a Dutch regional hospital, published by Eindhoven University
      of Technology. Each case is a patient arriving with symptoms suggestive of sepsis; every
      event is a clinical act (triage, blood test, antibiotic administration, admission,
      release). All identifiers are removed. The log is one of the most-cited benchmarks in
      healthcare process mining precisely because the real workflow is irregular and
      life-threatening.
    </>
  ),

  hero: {
    kicker: "Issue III  ·  The Patient Who Came Back",
    title: (
      <>
        A hospital admitted <span className="num-amber">1,050</span> suspected-sepsis patients.{" "}
        <span className="frac">One in seven</span> returned to the ER within twenty-eight days.
      </>
    ),
    dek: (
      <>
        Sepsis pathways are designed to be fast and linear — triage, labs, antibiotics,
        admission. The logs reveal a quieter, slower story: a minority of patients leave with a
        discharge code, then return days later with the same condition on record. The first
        visit saved them. The second visit asked why the first did not.
      </>
    ),
  },

  dotGrid: {
    total: 420,
    bad: 60,
    caption: (
      <>
        <b>1 dot = ~2.5 patients</b>
        420 dots represent all 1,050 suspected-sepsis admissions. <em>60 dots in red</em> are
        the patients who returned to the ER within four weeks — the single biggest quality
        signal in the log.
      </>
    ),
  },

  lede: (
    <>
      The clinical pathway is supposed to read like a reflex: the patient arrives, vitals are
      taken, sepsis is suspected, bloods are drawn, and antibiotics start inside the hour. The
      logs confirm that the first hour is, for the most part, fast. What the logs also show is
      that the first hour is not the whole story. A meaningful share of patients are discharged
      within a day, return within a week, and then stay in hospital for much longer the second
      time around.
    </>
  ),

  headlineFinding: {
    label: "· 01 · Headline finding",
    h2: "The first hour is fast. The first week is where the risk sits.",
    body: (
      <>
        Median time from <em>ER Triage</em> to <em>IV Antibiotics</em> is under ninety minutes —
        a strong result by any protocol. The hazard is elsewhere. Roughly <b>14% of patients</b>
        discharge within the first day and return to the ER within twenty-eight — and in that
        subset, the second admission is three times longer than the first. The log is not
        telling a story about emergency response. It is telling a story about{" "}
        <em>what happens after the reflex ends</em>.
      </>
    ),
    issues: [
      {
        tag: "Issue I · Quick Discharge",
        big: "14",
        unit: "%",
        ev: "Patients discharged within 24 hours who return to the ER inside four weeks with the same suspected-sepsis flag.",
      },
      {
        tag: "Issue II · Return Stay",
        big: "3.1",
        unit: "×",
        ev: "Median length of the second hospital stay versus the first — measured on the 147 patients who returned within 28 days.",
      },
      {
        tag: "Issue III · Missing Lactate",
        big: "41",
        unit: "%",
        ev: "Cases where the log-recorded bundle (CRP + leucocytes + lactate) is not complete before antibiotics start. The dataset's bundle proxy — not the Surviving Sepsis Campaign hour-1 protocol.",
      },
    ],
  },

  sectionTwo: {
    label: "· 02 · The clinical pathway vs. the patient trajectory",
    diagram: {
      exhibitTag: (<>Exhibit <b>· As-declared sepsis pathway</b> — with trajectory overlaid in amber</>),
      sourceTag: "Source: DFG over ~15k events",
      steps: [
        { num: "STEP 01", name: "Register",   subActs: ["ER Registration"],                 band: "teal"   },
        { num: "STEP 02", name: "Triage",     subActs: ["ER Triage", "ER Sepsis Triage"],   band: "muted"  },
        { num: "STEP 03", name: "Labs",       subActs: ["CRP / Leucocytes", "LacticAcid"],  band: "signal" },
        { num: "STEP 04", name: "Treat",      subActs: ["IV Antibiotics", "IV Liquid"],     band: "alert"  },
        { num: "STEP 05", name: "Admit",      subActs: ["Admission NC / IC", "Release A"],  band: "ink"    },
      ],
      gapDays: ["0.02 d", "0.04 d", "0.08 d", "0.4 d"],
      selfLoops: [{ fromStep: 3, label: "re-labs" }],
      topNotes: [
        { text: (<><tspan x="274" dy="0">median <tspan className="n">35 min</tspan> triage to</tspan><tspan x="274" dy="18">first lab result</tspan></>), fromStep: 2, tickColor: "muted", width: 230, x: 260, y: 70 },
        { text: (<><tspan className="n">41%</tspan> of bundles skip the lactate</>), fromStep: 3, tickColor: "signal", width: 230, x: 540, y: 86 },
        { text: (<><tspan x="814" dy="0"><tspan className="n">14%</tspan> return within</tspan><tspan x="814" dy="18">28 days — same flag</tspan></>), fromStep: 5, tickColor: "alert", width: 220, x: 800, y: 144 },
      ],
      cancelStub: {
        label: "Return ER · 147",
        fromStep: 5,
        note: (<><tspan className="n">147</tspan> readmissions — new ticket, same patient</>),
      },
      declaredMedianDays: "1.4 d",
      legend: [
        { band: "teal",   label: "Register · arrival" },
        { band: "muted",  label: "Triage · standard" },
        { band: "signal", label: "Labs · partial bundle" },
        { band: "alert",  label: "Treat · where the drugs start" },
        { band: "ink",    label: "Admit · outcome" },
      ],
    },
    sparkline: {
      title: "Time from discharge to return-ER visit",
      body: (
        <>
          For the patients who return, most come back within the first week — the{" "}
          <em>median return</em> is under ten days, and the curve decays fast after that. A
          genuinely early-warning signal for the discharge desk.
        </>
      ),
    },
  },

  sectionThree: {
    labelFromCount: (n) => `· 03 · ${toWord(n)} findings from the trajectory`,
  },

  sectionFive: {
    label: "· 05 · As a consultant, I'd recommend",
    h2: "Three protocol changes. Zero new equipment.",
    pull: "If the hospital closes the return-visit loop, the 147 readmissions a year stop being discovered at the door.",
    recs: [
      { tag: "R1", act: "Hard-require the log-recorded bundle (CRP + leucocytes + lactate) before antibiotics.", impact: (<><b>closes 41%</b> bundle gap</>) },
      { tag: "R2", act: "Schedule a 48-hour telephone review for every same-day discharge.",                     impact: (<><b>147</b> returns / yr in scope</>) },
      { tag: "R3", act: "Flag any patient returning within 28 days for senior review on arrival.",               impact: (<><b>flags</b> every return visit</>) },
    ],
    close: (
      <>
        The ER is doing its job in the first hour. The hospital's job after that — the follow-up
        call, the second opinion, the readmission flag — is where the log stops looking heroic
        and starts looking avoidable.
      </>
    ),
  },
};

function toWord(n: number): string {
  const words = ["zero","one","two","three","four","five","six","seven","eight","nine","ten","eleven","twelve"];
  return words[n] ?? String(n);
}
